import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import {
  UserCircleIcon,
  HomeModernIcon,
  CurrencyRupeeIcon,
  PencilSquareIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CheckCircleIcon,
  ArrowRightCircleIcon,
  PlusCircleIcon,
  IdentificationIcon
} from "@heroicons/react/24/solid";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [project, setProject] = useState(null);
  const [payments, setPayments] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data: profile } = await api.get('/users/profile');
        setProfileData(profile);
        const { data: projectsRes } = await api.get('/projects');
        const projects = projectsRes.data || [];
        setProject(projects.length > 0 ? projects[0] : null);
        const { data: paymentsRes } = await api.get('/payments');
        setPayments(paymentsRes.data || []);
        
        // Fetch construction phases to determine actual project status
        try {
          const { data: phasesRes } = await api.get('/construction/phases');
          setPhases(phasesRes || []);
        } catch (phasesErr) {
          console.error('Failed to fetch phases:', phasesErr);
          setPhases([]);
        }
      } catch (err) {
        setError("Could not load your profile. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // Payment summary logic
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const lastPayment = payments.length > 0 ? payments[0] : null;
  const nextDue = payments.find(p => p.status === 'due' || p.status === 'pending');

  // Determine project status based on construction phases
  const getProjectStatus = () => {
    if (!phases || phases.length === 0) {
      return project?.status || 'Unknown';
    }

    const completedPhases = phases.filter(phase => 
      (phase.progress ?? 0) >= 100 || 
      phase.status === "Completed" || 
      phase.status === "Complete"
    );
    
    const inProgressPhases = phases.filter(phase => 
      (phase.progress ?? 0) > 0 && 
      (phase.progress ?? 0) < 100 && 
      phase.status !== "Completed" && 
      phase.status !== "Complete"
    );

    if (completedPhases.length === phases.length) {
      return 'Completed';
    } else if (inProgressPhases.length > 0) {
      return 'In Progress';
    } else if (completedPhases.length > 0) {
      return 'In Progress'; // Some phases completed, others not started
    } else {
      return 'Planning'; // No phases started yet
    }
  };

  // Project status badge
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-50 text-green-600';
      case 'in progress': return 'bg-blue-50 text-blue-600';
      case 'planning': return 'bg-yellow-50 text-yellow-700';
      case 'on-hold': return 'bg-yellow-50 text-yellow-700';
      case 'cancelled': return 'bg-red-50 text-red-600';
      default: return 'bg-gray-50 text-gray-500';
    }
  };

  // Customer ID formatting
  const getShortId = (id) => id ? `${id.slice(0, 4)}...${id.slice(-4)}` : 'N/A';

  // Avatar upload handler
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setAvatar(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-500">Loading Your Profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-700 mb-2">Error Loading Profile</h2>
          <p className="text-gray-500 mb-4">{error || 'Profile data not available'}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold/80 text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold transition-colors"
            aria-label="Try Again"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const user = profileData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-palamuru-cream">
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-10 flex flex-col items-center">
          <h1 className="text-4xl md:text-5xl font-display-bold text-gray-800 text-center mb-2">Welcome, {user.full_name || 'Customer'}</h1>
          <p className="text-lg text-gray-500 text-center">This is your personal construction journey dashboard.</p>
        </div>
        {/* Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Personal Details Card */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8 flex flex-col items-center">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full border-4 border-white shadow-md bg-gradient-to-br from-palamuru-saffron/80 to-palamuru-gold/80 flex items-center justify-center -mt-16 mb-4">
              {avatar ? (
                <img src={avatar} alt="Avatar" className="w-full h-full object-cover rounded-full" />
              ) : (
                <UserCircleIcon className="h-14 w-14 text-white/90" />
              )}
            </div>
            <div className="text-center w-full">
              <div className="text-2xl font-bold text-gray-700 mb-1">{user.full_name || 'Not provided'}</div>
              <div className="text-base text-gray-500 mb-4">{user.email || 'Not provided'}</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-500 justify-center"><PhoneIcon className="h-5 w-5" />{user.phone || 'Not provided'}</div>
                <div className="flex items-center gap-2 text-gray-500 justify-center"><MapPinIcon className="h-5 w-5" />{user.address || 'Not provided'}</div>
                <div className="flex items-center gap-2 text-gray-500 justify-center"><IdentificationIcon className="h-5 w-5" />Customer ID: <span title={user.id}>{getShortId(user.id)}</span></div>
              </div>
            </div>
          </div>
          {/* Project Summary Card */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <HomeModernIcon className="h-7 w-7 text-blue-300" />
              <div className="text-xl font-bold text-gray-700">Project Summary</div>
            </div>
            {project ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Project Name:</span> <span className="text-gray-600">{project.name}</span></div>
                <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Site Location:</span> {project.location_address || 'Not provided'}</div>
                <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Start Date:</span> {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'Not provided'}</div>
                <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Est. Completion:</span> {project.expected_end_date ? new Date(project.expected_end_date).toLocaleDateString() : 'Not provided'}</div>
                <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Status:</span> <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(getProjectStatus())}`}>{getProjectStatus()}{getProjectStatus() === 'Completed' && <CheckCircleIcon className="h-4 w-4 ml-1" />}</span></div>
                <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Next Milestone:</span> {phases && phases.length > 0 ? (phases.find(p => (p.progress ?? 0) < 100)?.name || 'All Complete') : 'N/A'}</div>
              </div>
            ) : (
              <div className="text-gray-400">No project information available.</div>
            )}
          </div>
          {/* Payment Summary Card */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <CurrencyRupeeIcon className="h-7 w-7 text-blue-300" />
              <div className="text-xl font-bold text-gray-700">Payment Summary</div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Total Paid:</span> <span className="text-gray-600">₹{totalPaid.toLocaleString('en-IN')}</span></div>
              <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Last Payment:</span> {lastPayment ? <span className="text-gray-600">₹{Number(lastPayment.amount).toLocaleString('en-IN')}</span> : <span className="text-gray-400">N/A</span>}</div>
              <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Next Payment Due:</span> {nextDue ? <span className="text-gray-600">₹{Number(nextDue.amount).toLocaleString('en-IN')} ({new Date(nextDue.due_date || nextDue.created_at).toLocaleDateString()})</span> : <span className="text-gray-400">N/A</span>}</div>
              <div className="flex items-center gap-2 text-gray-500"><span className="font-medium text-gray-700">Status:</span> <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(nextDue?.status || lastPayment?.status)}`}>{nextDue?.status || lastPayment?.status || 'N/A'}</span></div>
            </div>
            <div className="mt-6">
              <button 
                onClick={() => navigate('/payments')}
                className="w-full bg-palamuru-blue text-white py-3 px-4 rounded-xl font-body-semibold hover:bg-palamuru-blue-dark transition-colors focus:outline-none focus:ring-2 focus:ring-palamuru-blue focus:ring-offset-2 text-base"
                aria-label="View Payments"
              >
                View Payments & History
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable card component
const InfoCard = ({ children }) => (
  <div className="bg-white/90 rounded-2xl shadow-md border border-gray-100 p-5 md:p-6 flex flex-col justify-between min-h-[200px] w-full max-w-xs mx-auto transition-shadow hover:shadow-lg focus-within:shadow-lg">
    {children}
  </div>
);

// Reusable info row (now supports sublabel)
const InfoRow = ({ icon, label, value, sublabel, action }) => (
  <div className="flex items-center justify-between text-base py-1 w-full">
    <div className="flex items-center gap-2 text-gray-400 font-medium">
      {icon}
      <span>{label}</span>
    </div>
    <div className="flex flex-col items-end">
      <div className="text-gray-600 font-body-semibold flex items-center gap-2">{value}{action}</div>
      {sublabel && <div>{sublabel}</div>}
    </div>
  </div>
);

export default ProfilePage; 